import React, { useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Panel, Empty, StatusBadge, StatCard, Disclaimer } from '../../components/Primitives';
import { useFetch } from '../../lib/hooks';
import { api, errMsg, fmtUsd, fmtDate, shortHash } from '../../lib/api';

export default function Withdrawals() {
  const { data: summary } = useFetch('/account/summary', [], 20000);
  const { data: addrs } = useFetch('/withdrawal-addresses');
  const { data: list, reload } = useFetch('/withdrawals', [], 20000);
  const [addressId, setAddressId] = useState('');
  const [amount, setAmount] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!addressId) return toast.error('Select a withdrawal address');
    if (!(Number(amount) > 0)) return toast.error('Enter a valid amount');
    try {
      await api.post('/withdrawals', { address_id: addressId, amount });
      toast.success('Withdrawal requested — pending approval');
      setAmount(''); reload();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6" data-testid="withdrawals-page">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Withdrawable" value={fmtUsd(summary?.withdrawable)} accent="emerald" />
        <StatCard label="Reserved" value={fmtUsd(summary?.reserved)} accent="gold" />
        <StatCard label="Pending Withdrawals" value={fmtUsd(summary?.pending_withdrawals)} accent="silver" />
      </div>

      <Panel title="Request Withdrawal">
        {addrs && addrs.length ? (
          <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3">
            <select value={addressId} onChange={(e) => setAddressId(e.target.value)} className="h-11 px-3 rounded-lg bg-vj-charcoal border border-border text-sm text-foreground" data-testid="withdraw-address-select">
              <option value="">Select address…</option>
              {addrs.map((a) => <option key={a.id} value={a.id}>{a.label} ({shortHash(a.address)})</option>)}
            </select>
            <input type="number" step="0.00000001" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount (USDT)" className="h-11 px-3 rounded-lg bg-vj-charcoal border border-border font-mono text-sm text-foreground" data-testid="withdraw-amount-input" />
            <button className="h-11 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm vj-glow-green hover:-translate-y-0.5 transition-all" data-testid="withdraw-submit-btn">Request Withdrawal</button>
          </form>
        ) : <Empty>Add a verified withdrawal address first in <Link to="/dashboard/wallet" className="text-vj-neon">Wallet</Link>.</Empty>}
      </Panel>

      <Panel title="Withdrawal History">
        {list && list.length ? (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-vj-grey border-b border-border"><th className="py-2 font-medium">Amount</th><th className="font-medium">To</th><th className="font-medium">Status</th><th className="font-medium">Tx</th><th className="font-medium">Date</th></tr></thead>
            <tbody>{list.map((w) => (
              <tr key={w.id} className="border-b border-border last:border-0">
                <td className="py-3 font-mono">{fmtUsd(w.amount)}</td>
                <td className="text-xs">{w.address_label}</td>
                <td><StatusBadge status={w.status} /></td>
                <td>{w.tx_hash ? <a href={`https://bscscan.com/tx/${w.tx_hash}`} target="_blank" rel="noreferrer" className="text-vj-neon text-xs inline-flex items-center gap-1 font-mono">{shortHash(w.tx_hash)}<ExternalLink size={11} /></a> : <span className="text-vj-grey text-xs">—</span>}</td>
                <td className="text-xs text-vj-grey">{fmtDate(w.created_at)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        ) : <Empty>No withdrawal requests yet.</Empty>}
      </Panel>

      <Disclaimer>Approval is not payment. A withdrawal is marked completed only after a verified on-chain transfer. On-chain signing runs in development mode until a secure treasury signer is configured.</Disclaimer>
    </div>
  );
}
